/**
 * SMSScheduledView - Scheduled SMS management
 */
import { Calendar, Users, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CAMPAIGN_NAMES } from './smsUtils';

export default function SMSScheduledView({
  scheduledSMS,
  message,
  setMessage,
  recipientType,
  setRecipientType,
  phoneNumber,
  setPhoneNumber,
  campaignId,
  setCampaignId,
  scheduleDateTime,
  setScheduleDateTime,
  isScheduling,
  onScheduleSMS,
  onCancelScheduledSMS,
  onRefresh
}) {
  return (
    <div className="space-y-6">
      {/* Schedule New SMS */}
      <div className="bg-card rounded-2xl p-6">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Schedule New Broadcast
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* Recipient Type */}
            <div className="space-y-2">
              <Label className="text-foreground">Send To</Label>
              <Select value={recipientType} onValueChange={setRecipientType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual Number</SelectItem>
                  <SelectItem value="campaign">Campaign Signups</SelectItem>
                  <SelectItem value="loyalty">Loyalty Members</SelectItem>
                  <SelectItem value="all">All Subscribers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {recipientType === 'individual' && (
              <div className="space-y-2">
                <Label className="text-foreground">Phone Number</Label>
                <Input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+6791234567"
                />
              </div>
            )}

            {recipientType === 'campaign' && (
              <div className="space-y-2">
                <Label className="text-foreground">Campaign</Label>
                <Select value={campaignId} onValueChange={setCampaignId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Campaigns</SelectItem>
                    <SelectItem value="1">Founding Members</SelectItem>
                    <SelectItem value="2">Island Energy</SelectItem>
                    <SelectItem value="3">Grand Opening</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Schedule Date/Time */}
            <div className="space-y-2">
              <Label className="text-foreground">Schedule Date & Time</Label>
              <Input
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                data-testid="schedule-datetime-input"
              />
            </div>
          </div>

          <div className="space-y-4">
            {/* Message */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-foreground">Message</Label>
                <span className="text-xs text-muted-foreground">{message.length} chars</span>
              </div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your scheduled message..."
                rows={4}
                className="resize-none"
              />
            </div>

            <Button
              onClick={onScheduleSMS}
              disabled={isScheduling || !message.trim() || !scheduleDateTime}
              className="w-full"
              data-testid="schedule-sms-btn"
            >
              {isScheduling ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule SMS
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Scheduled SMS List */}
      <div className="bg-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-foreground">Scheduled Broadcasts</h3>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {scheduledSMS.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No scheduled broadcasts</p>
          </div>
        ) : (
          <div className="space-y-3">
            {scheduledSMS.map(scheduled => (
              <div 
                key={scheduled.id} 
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  scheduled.status === 'scheduled' 
                    ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                    : scheduled.status === 'completed'
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                    : scheduled.status === 'cancelled'
                    ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                    : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-foreground">
                      {scheduled.recipient_type === 'campaign' 
                        ? `Campaign: ${CAMPAIGN_NAMES[scheduled.campaign_id] || 'All'}`
                        : scheduled.recipient_type === 'loyalty'
                        ? 'Loyalty Members'
                        : scheduled.recipient_type === 'individual'
                        ? scheduled.phone_number
                        : 'All Subscribers'
                      }
                    </p>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      scheduled.status === 'scheduled' 
                        ? 'bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200'
                        : scheduled.status === 'completed'
                        ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                        : scheduled.status === 'cancelled'
                        ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        : 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                    }`}>
                      {scheduled.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-1 mb-1">
                    {scheduled.message}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(scheduled.scheduled_time).toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {scheduled.recipient_count} recipients
                    </span>
                    {scheduled.sent_count !== undefined && (
                      <span className="text-green-600">{scheduled.sent_count} sent</span>
                    )}
                  </div>
                </div>
                {scheduled.status === 'scheduled' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCancelScheduledSMS(scheduled.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
